import { DateTime } from "luxon";

import {
  ExperienceActionDto,
  ExperienceCategory,
  ExperienceStatus,
} from "@intelligems/ig-types";
import {
  getEndedAtTs,
  getPausedAtTs,
  getStartedAtTs,
} from "@intelligems/ig-analytics";
import { ensureNumber } from "@intelligems/ig-utils";

// `Intl.DateTimeFormat#format` throws `RangeError: Invalid time value` when given a
// numeric timestamp outside the valid Date range (±8.64e15 ms, e.g. a value supplied in
// microseconds/nanoseconds). Treat such values the same as a missing date so callers get
// the graceful fallback instead of crashing the surrounding route.
const isRenderableTimestamp = (date: number): boolean =>
  Number.isFinite(new Date(date).getTime());

export const fullDateTimeConverter = function (
  date: number | string | null | undefined,
  timeZone?: string,
  locale?: string,
  hideTimeZone?: boolean
): string | null {
  // Mirror the sibling converters: an out-of-range or non-numeric timestamp
  // makes `Intl.DateTimeFormat#format` throw `RangeError: Invalid time value`
  // (Sentry WEB-UI-5FN). Return the graceful `null` fallback callers already
  // handle (`?? "—"`) instead of crashing the surrounding route.
  const numericDate = ensureNumber(date);
  if (!isRenderableTimestamp(numericDate)) return null;

  if (timeZone) {
    return new Intl.DateTimeFormat(locale || "en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: hideTimeZone ? undefined : "short",
      timeZone,
    }).format(numericDate);
  } else {
    return new Intl.DateTimeFormat(locale || "en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: hideTimeZone ? undefined : "short",
    }).format(numericDate);
  }
};

export const fullDateTimeConverterCommas = function ({
  date,
  timeZone,
  locale,
  showTimeZoneName,
}: {
  date: number | string | null | undefined;
  timeZone?: string;
  locale?: string;
  showTimeZoneName?: boolean;
}) {
  if (!date) return null;

  const numericDate = typeof date === "string" ? Number(date) : date;
  // `isNaN` alone misses out-of-range timestamps (e.g. micro/nanosecond values);
  // `formatToParts(new Date(...))` throws `RangeError: Invalid time value` on those.
  if (!isRenderableTimestamp(numericDate)) return null;

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };

  const formatter = new Intl.DateTimeFormat(locale || "en-US", {
    ...options,
    timeZone,
    timeZoneName: showTimeZoneName ? "shortGeneric" : undefined,
  });

  const parts = formatter.formatToParts(new Date(numericDate));

  // Rebuild the string with commas but avoid the comma after the year
  let result = "";
  parts.forEach((part, index) => {
    if (
      part.type === "literal" &&
      part.value === ", " &&
      parts[index - 1]?.type === "year"
    ) {
      // Skip the comma that follows the year
      result += " ";
    } else {
      result += part.value;
    }
  });

  return result;
};

export const dateTimeConverter = (
  date: number | null | undefined,
  locale?: string,
  timeZone?: string,
  showOngoing?: boolean,
  useLeadingZeros?: boolean
) => {
  if (!date || !isRenderableTimestamp(date))
    return showOngoing ? "Ongoing" : null;

  const monthFormat = useLeadingZeros ? "2-digit" : "numeric";
  const dayFormat = useLeadingZeros ? "2-digit" : "numeric";

  if (timeZone) {
    return new Intl.DateTimeFormat(locale || "en-US", {
      year: "2-digit",
      month: monthFormat,
      day: dayFormat,
      timeZone: timeZone,
    }).format(date);
  } else {
    return new Intl.DateTimeFormat(locale || "en-US", {
      year: "2-digit",
      month: monthFormat,
      day: dayFormat,
    }).format(date);
  }
};

export const shortDateTimeConverter = (
  date: number | null | undefined,
  locale?: string,
  timeZone?: string,
  showOngoing?: boolean,
  showYear?: boolean,
  showTimeZoneName?: boolean
) => {
  if (!date || !isRenderableTimestamp(date))
    return showOngoing ? "Ongoing" : null;
  if (timeZone) {
    return new Intl.DateTimeFormat(locale || "en-US", {
      month: "short",
      day: "numeric",
      timeZoneName: showTimeZoneName ? "short" : undefined,
      timeZone: timeZone,
      year: showYear ? "numeric" : undefined,
    }).format(date);
  } else {
    return new Intl.DateTimeFormat(locale || "en-US", {
      month: "short",
      day: "numeric",
      year: showYear ? "numeric" : undefined,
    }).format(date);
  }
};

export const analyticsTooltipTimeConverter = (
  date: number,
  locale?: string,
  timeZone?: string
) => {
  if (timeZone) {
    return new Intl.DateTimeFormat(locale || "en-US", {
      month: "long",
      day: "numeric",
      // timeZoneName: "long",
      timeZone: timeZone,
      year: undefined,
    }).format(date);
  } else {
    return new Intl.DateTimeFormat(locale || "en-US", {
      month: "long",
      day: "numeric",
      year: undefined,
    }).format(date);
  }
};

const getDaysBetweenDates = (startTime: number, endTime: number) => {
  const start = new Date(Math.floor(startTime));
  const end = new Date(Math.floor(endTime));
  const diffInTime = end.getTime() - start.getTime();
  return diffInTime / (1000 * 3600 * 24);
};

export const getExperienceRunTime = (
  experienceStatus: ExperienceStatus,
  experienceActions: ExperienceActionDto[],
  timezone: string | null | undefined,
  category: ExperienceCategory,
  showDays: boolean = false
) => {
  const timeNow = DateTime.now()
    .setZone(timezone ?? "UTC")
    .toMillis();
  if (experienceStatus === "pending") {
    return "";
  } else {
    const latestStartedTs = getStartedAtTs(experienceActions);
    const latestEndedAt =
      experienceStatus === "paused"
        ? getPausedAtTs(experienceActions)!.actionTs
        : experienceStatus === "ended"
          ? getEndedAtTs(experienceActions)!.actionTs
          : timeNow;

    if (latestStartedTs?.actionTs) {
      const daysBetween = getDaysBetweenDates(
        latestStartedTs?.actionTs,
        latestEndedAt
      );

      const formattedStartDate = DateTime.fromMillis(latestStartedTs.actionTs)
        .setZone(timezone ?? "UTC")
        .toFormat("MMM d");
      const formattedEndDate = DateTime.fromMillis(latestEndedAt)
        .setZone(timezone ?? "UTC")
        .toFormat("MMM d");

      if (daysBetween < 1) {
        return "Running time: <1 day";
      }

      const durationText = showDays
        ? `${Math.floor(daysBetween)} day${Math.floor(daysBetween) === 1 ? "" : "s"}`
        : "";
      const dateRange = `${formattedStartDate} - ${formattedEndDate}`;

      return `Running time: ${durationText ? `${durationText} (${dateRange})` : dateRange}`;
    }
    return "";
  }
};

export const getExperienceRunTimeNumeric = ({
  experienceStatus,
  experienceActions,
  timezone,
  dashboardStartTs,
  dashboardEndTs,
}: {
  experienceStatus: ExperienceStatus;
  experienceActions: ExperienceActionDto[];
  timezone: string | null | undefined;
  dashboardStartTs?: number | null;
  dashboardEndTs?: number | null;
}) => {
  const timeNow = DateTime.now()
    .setZone(timezone ?? "UTC")
    .toMillis();
  if (experienceStatus === "pending") {
    return 0;
  } else {
    let latestStartedTs = getStartedAtTs(experienceActions)?.actionTs;
    let latestEndedAt =
      experienceStatus === "paused"
        ? getPausedAtTs(experienceActions)!.actionTs
        : experienceStatus === "ended"
          ? getEndedAtTs(experienceActions)!.actionTs
          : timeNow;
    if (dashboardStartTs) {
      latestStartedTs = dashboardStartTs;
    }
    if (dashboardEndTs) {
      latestEndedAt = dashboardEndTs;
    }
    if (latestStartedTs) {
      return getDaysBetweenDates(latestStartedTs, latestEndedAt);
    }
    return 0;
  }
};

export const getPersonalizationOrDefaultRunTimeNumeric = ({
  experienceStatus,
  experienceActions,
  timezone,
  dashboardStartTs,
  dashboardEndTs,
}: {
  experienceStatus: ExperienceStatus;
  experienceActions: ExperienceActionDto[];
  timezone: string | null | undefined;
  dashboardStartTs?: number | null;
  dashboardEndTs?: number | null;
}) => {
  const timeNow = DateTime.now()
    .setZone(timezone ?? "UTC")
    .toMillis();
  if (experienceStatus === "pending") {
    return 0;
  } else {
    let latestStartedTs = getStartedAtTs(experienceActions)?.actionTs;
    let latestEndedAt =
      experienceStatus === "paused"
        ? getPausedAtTs(experienceActions)!.actionTs
        : timeNow;
    if (dashboardStartTs) {
      latestStartedTs = dashboardStartTs;
    }
    if (dashboardEndTs) {
      latestEndedAt = dashboardEndTs;
    }
    if (latestStartedTs) {
      const daysBetween = getDaysBetweenDates(latestStartedTs, latestEndedAt);
      return experienceStatus === "paused" &&
        !dashboardStartTs &&
        !dashboardEndTs &&
        daysBetween > 30
        ? 30
        : daysBetween;
    }
    return 0;
  }
};

export const getTimeAgo = (time: number | string, now: number): string => {
  let timestamp: number;

  if (typeof time === "string") {
    // Handle the format "YYYY-MM-DD HH:MM:SS.sss" as UTC
    if (time.includes(" ") && !time.includes("T")) {
      const [datePart, timePart] = time.split(" ");
      const utcString = `${datePart}T${timePart}Z`;
      timestamp = new Date(utcString).getTime();
    } else {
      // Handle other formats
      timestamp = new Date(time).getTime();
    }
  } else {
    timestamp = time;
  }

  if (!timestamp) return "";

  const diffInSeconds = Math.abs(Math.floor((now - timestamp) / 1000));

  if (isNaN(diffInSeconds) || !diffInSeconds) {
    return "";
  }

  if (diffInSeconds < 60) {
    return `${diffInSeconds} sec ago`;
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} min ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours}h ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}d ago`;
  }
};

export { getDaysBetweenDates };
